import { fetchAuthSession } from "aws-amplify/auth";
import ApiService from "./ApiService";
import { setUserId } from "./GoogleAnalytics";

/** Cognito ID token `identities` is often a JSON string; Amplify may expose an array. */
function parseFederatedIdentities(identities) {
    if (!identities) {
        return [];
    }
    if (Array.isArray(identities)) {
        return identities;
    }
    if (typeof identities === "string") {
        try {
            const parsed = JSON.parse(identities);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

/**
 * Federated subs and signup source for `/home/user/after-signup` (Google / Sign in with Apple).
 * See Cognito ID token identities: userId + providerName (e.g. Google, SignInWithApple).
 */
function federatedFromIdentities(identities) {
    const list = parseFederatedIdentities(identities);
    const ordered = [...list].sort((a, b) => {
        const ap = a.primary === true || a.primary === "true";
        const bp = b.primary === true || b.primary === "true";
        if (ap && !bp) {
            return -1;
        }
        if (!ap && bp) {
            return 1;
        }
        return 0;
    });

    let googleSub = null;
    let appleSignInSub = null;
    let signupSource = null;

    for (const id of ordered) {
        const name = String(id.providerName || id.providerType || "");
        const userId = id.userId != null ? String(id.userId).trim() : "";
        if (!userId) {
            continue;
        }
        if (name === "Google" || /^google$/i.test(name)) {
            googleSub = googleSub || userId;
            if (!signupSource) {
                signupSource = "GOOGLE";
            }
        } else if (
            name === "SignInWithApple" ||
            name === "Apple" ||
            /^sign\s*in\s*with\s*apple$/i.test(name)
        ) {
            appleSignInSub = appleSignInSub || userId;
            if (!signupSource) {
                signupSource = "APPLE";
            }
        }
    }

    return { googleSub, appleSignInSub, signupSource };
}

function buildAfterSignupBody(payload) {
    const { googleSub, appleSignInSub, signupSource } = federatedFromIdentities(payload.identities);

    const body = {
        email: payload.email,
        "cognito:username": payload["cognito:username"] ?? payload.username,
        email_verified: payload.email_verified,
        given_name: payload.given_name,
        family_name: payload.family_name,
    };

    if (googleSub) {
        body.googleSub = googleSub;
    }
    if (appleSignInSub) {
        body.appleSignInSub = appleSignInSub;
    }
    // Explicit WEB for email/password and other non-federated web signups; GOOGLE / APPLE when identities say so.
    body.signupSource = signupSource ?? "WEB";

    return body;
}

const UserService = {
    getUserInfo: async () => {
        try {
            const session = await fetchAuthSession();

            if (!session?.tokens) {
                return;
            }

            const { idToken, accessToken } = session.tokens;
            const user = idToken.payload;

            let cloudUser = await ApiService.getData(`/home/user`);
            if (cloudUser && cloudUser.code == "NO_DATA") {
                cloudUser = await ApiService.postData(
                    `/home/user/after-signup`,
                    buildAfterSignupBody(user)
                );
            }

            const userInfo = {
                user: user,
                cloudUser: cloudUser,
                accessToken: accessToken,
            };

            // Set Google Analytics user ID
            if (cloudUser?.email) {
                setUserId(cloudUser.email);
            }

            return userInfo;
        } catch (error) {
            console.error("Error fetching user info:", error);
            throw error;
        }
    },

    updateUsername: async (cloudUserId, firstName, lastName) => {
        try {
            if (!cloudUserId) {
                throw new Error('User ID is required');
            }

            const response = await ApiService.putData(
                `/home/user/name`,
                { firstName, lastName }
            );

            if (response && response.code !== 'FAILURE') {
                return response;
            } else {
                throw new Error(response?.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    },

    // Update user profile details (handles both profile and settings data)
    updateUserProfile: async (cloudUserId, data) => {
        try {
            if (!cloudUserId) {
                throw new Error('User ID is required');
            }

            const response = await ApiService.putData(
                `/home/user/details`,
                data
            );

            if (response && response.code !== 'FAILURE') {
                return response;
            } else {
                throw new Error(response?.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    },

    // Get profile form data from user info
    getProfileFormData: (userInfo) => {
        return {
            firstName: userInfo?.cloudUser?.firstName || '',
            lastName: userInfo?.cloudUser?.lastName || '',
            avatarUrl: userInfo?.cloudUser?.avatarUrl || '',
            avatarFileName: userInfo?.cloudUser?.avatarFileName || ''
        };
    },

    /** Coerce API boolean settings (may be string/number) with a default when absent. */
    settingsBoolean(value, defaultValue = true) {
        if (value === undefined || value === null) return defaultValue;
        if (value === false || value === "false" || value === 0 || value === "0") return false;
        if (value === true || value === "true" || value === 1 || value === "1") return true;
        return defaultValue;
    },

    // Get settings form data from user info
    getSettingsFormData: (userInfo) => {
        const details = userInfo?.cloudUser?.details;
        return {
            notifications: UserService.resolvePushNotificationsEnabled(details),
            manualValidation: userInfo?.cloudUser?.details?.manualValidation ?? false,
            soundEnabled: userInfo?.cloudUser?.details?.soundEnabled ?? false,
            bgmAutoPlay: UserService.settingsBoolean(details?.bgmAutoPlay, false),
            pdfFormat: userInfo?.cloudUser?.details?.pdfFormat,
            pdfDictionary: userInfo?.cloudUser?.details?.pdfDictionary ?? false
        };
    },

    /**
     * Push opt-in from details.notificationsByApp (default true when unset).
     * Web settings toggle is on if any known app is true / unset.
     */
    resolvePushNotificationsEnabled: (details) => {
        const byApp = details?.notificationsByApp;
        if (!byApp || typeof byApp !== "object") {
            return true;
        }
        const apps = ["jigsaw_muse", "sliding_puzzle_quest", "word_wheel_quest"];
        let saw = false;
        for (const app of apps) {
            if (byApp[app] === undefined || byApp[app] === null) {
                continue;
            }
            saw = true;
            if (byApp[app] === true || byApp[app] === "true" || byApp[app] === 1) {
                return true;
            }
        }
        return !saw;
    },

    /** Payload to persist web/PWA push preference for all known apps. */
    notificationsByAppPayload: (enabled) => ({
        notificationsByApp: {
            jigsaw_muse: Boolean(enabled),
            sliding_puzzle_quest: Boolean(enabled),
            word_wheel_quest: Boolean(enabled),
        },
        notifications: null,
    }),

    /** Sidebar / nav label: full name when present, else email (cloud or ID token), else "User". */
    getNavDisplayName: (userInfo) => {
        const looksLikeEmail = (value) =>
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

        const nameWithoutEmbeddedEmail = (value) => {
            const trimmed = String(value).trim();
            if (!trimmed || looksLikeEmail(trimmed)) {
                return "";
            }
            const embedded = trimmed.match(/^(.+?)\s+([^\s@]+@[^\s@]+\.[^\s@]+)$/);
            return (embedded?.[1] ?? trimmed).trim();
        };

        const cloud = userInfo?.cloudUser;
        const token = userInfo?.user;
        if (!cloud) {
            return "User";
        }
        const details = cloud.details || {};
        const first = nameWithoutEmbeddedEmail(
            cloud.firstName ?? details.firstName ?? cloud.first_name ?? token?.given_name ?? ""
        );
        const last = nameWithoutEmbeddedEmail(
            cloud.lastName ?? details.lastName ?? cloud.last_name ?? token?.family_name ?? ""
        );
        const fullName = [first, last].filter(Boolean).join(" ");
        if (fullName) {
            return fullName;
        }
        const email = cloud.email ?? token?.email ?? "";
        return email || "User";
    }
};

export default UserService;
